import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ENTITIES } from '../data';

type EntityId = keyof typeof ENTITIES;

interface ReferencePage {
  title: string;
  text: string;
  terms: [EntityId, ...EntityId[]];
  path: string;
}

const REFERENCE: ReferencePage[] = [
  {
    title: 'API key capabilities and limits',
    text: 'What READ, WRITE, SUPPORT_TOKEN_MINT, FORM_SUBMIT and KNOWLEDGE_WRITE unlock, key scope, default rate limits and the 429 response, rotation, revocation and tenant binding.',
    terms: ['apiKey'],
    path: '/docs/reference/api-keys',
  },
  {
    title: 'Claude MCP tool catalogue',
    text: 'All 39 tools the MCP server exposes (20 read, 19 write), grouped by area, with which need approval in Revvly and where the connection guide differs.',
    terms: ['apiKey'],
    path: '/docs/reference/mcp-tools',
  },
  {
    title: 'What the Revvly Assistant can do',
    text: 'The 66 Assistant tools by area (31 read, 10 low-risk writes, 25 guarded writes), plus approval cards, 15-minute expiry, stale-edit protection and attachment limits.',
    terms: ['client', 'subscription'],
    path: '/docs/reference/assistant-tools',
  },
  {
    title: 'Integrations and availability',
    text: 'Every integration and capability with a plain status: Live, Live with limits, Needs set-up by Revvly, or Not available yet. Includes what Support does and does not cover.',
    terms: ['apiKey', 'tenant'],
    path: '/docs/reference/integrations',
  },
  {
    title: 'Terms and names',
    text: 'One-line definitions of Revvly vocabulary, with renames (Meter, Regions, Companies) and the old names that remain in the API.',
    terms: ['meter', 'region', 'client'],
    path: '/docs/reference/terms',
  },
  {
    title: 'Exports and reports',
    text: 'Every export and download: where it lives, format, who can run it, what it contains and which ones exist only in the API.',
    terms: ['invoice', 'subscription'],
    path: '/docs/reference/exports',
  },
  {
    title: 'Workspace features',
    text: 'Every workspace feature, what it gates, who can grant it, and what you see when it is off. Product feature flags are covered separately at the end.',
    terms: ['tenant', 'featureFlag'],
    path: '/docs/reference/features',
  },
  {
    title: 'What runs automatically',
    text: 'Every scheduled job and sweep you can observe: when it runs, what it does, what you see, and how to change or switch it off.',
    terms: ['subscription', 'subscriptionUsage'],
    path: '/docs/reference/automatic-jobs',
  },
  {
    title: 'Revenue metrics',
    text: 'The definition and formula behind MRR, ARR, net figures, NRR, churn, expansion, the movement waterfall, current month progress, currency and snapshots.',
    terms: ['lifecycleEvent', 'exchangeRate', 'subscription'],
    path: '/docs/reference/metrics',
  },
  {
    title: 'Limits and defaults',
    text: 'Every verified cap, rate limit, size, retention window and default, with where it is enforced and whether a workspace can change it.',
    terms: ['apiKey', 'tenant'],
    path: '/docs/reference/limits',
  },
];

export function ReferenceIndex() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Reference — Revvly Docs';
  }, []);
  return (
    <div className="tn-page">
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Reference</span>
        <h1>
          Facts, <em>limits</em> and definitions.
        </h1>
        <p className="dp-oneliner">
          Look-up pages: what each setting and flag does, what runs automatically, how the numbers
          are calculated, and what is and is not available. For step-by-step tasks, see the{' '}
          <Link to="/docs/guides">guides</Link>.
        </p>
      </header>
      <div className="tn-guide-grid">
        {REFERENCE.map((r) => (
          <Link key={r.path} className="tn-guide-card" to={r.path}>
            <span className="rv-eyebrow-mini">Reference</span>
            <h3>{r.title}</h3>
            <p>{r.text}</p>
            <div className="tn-guide-tags">
              {r.terms.map((t) => (
                <span key={t} className="tn-guide-tag">
                  {ENTITIES[t]?.name || t}
                </span>
              ))}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
